import moment from 'moment';
import 'moment/locale/fr';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Nav from '../../components/Nav';
import { useAxios } from '../../utils/hook/useAxios';
import './mc-account-locations.css';

moment.locale('fr');

const STATUS_TABS = [
  { key: 'all', label: 'Toutes' },
  { key: 'pending', label: 'En attente' },
  { key: 'accepted', label: 'Acceptées' },
  { key: 'paid', label: 'Confirmées' },
  { key: 'checkin_pending_validation', label: 'Check-in' },
  { key: 'in_progress', label: 'En cours' },
  { key: 'completed', label: 'Terminées' },
  { key: 'rejected', label: 'Refusées' },
];

const STATUS_BADGE = {
  pending: { tone: 'warn', label: 'En attente' },
  accepted: { tone: 'info', label: 'Acceptée' },
  paid: { tone: 'primary', label: 'Confirmée' },
  checkin_pending_validation: { tone: 'warn', label: 'Check-in' },
  in_progress: { tone: 'ok', label: 'En cours' },
  completed: { tone: 'ok', label: 'Terminée' },
  rejected: { tone: 'danger', label: 'Refusée' },
  cancelled: { tone: 'neutral', label: 'Annulée' },
};

function formatDays(start, end) {
  const days = moment(end).diff(moment(start), 'days');
  return days < 1 ? "moins d'1 j" : `${days} j`;
}

function Locations() {
  const axios = useAxios();
  const userData = JSON.parse(window.localStorage.getItem('me'));
  const [reservations, setReservations] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    localStorage.setItem('mode', 'host');
    const fetchReservations = async () => {
      try {
        const resp = await axios.get('/reservations/getbydriverid/' + userData?.id);
        if (resp.data) {
          setReservations(
            [...resp.data].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReservations();
  }, []);

  const filtered =
    activeTab === 'all' ? reservations : reservations.filter((r) => r.status === activeTab);

  const countByTab = (key) =>
    key === 'all' ? reservations.length : reservations.filter((r) => r.status === key).length;

  return (
    <main className="mc-home mc-account-page">
      <div className="container mc-container">
        <Nav />

        <div className="mc-account-shell">
          <div className="mc-account-head">
            <h1 className="mc-account-title">Mes locations</h1>
            {!isLoading && (
              <p className="mc-account-count" aria-live="polite">
                <strong>{filtered.length}</strong> réservation{filtered.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Onglets statut */}
          <div className="mc-loc-tabs" role="group" aria-label="Filtrer par statut">
            {STATUS_TABS.map(({ key, label }) => {
              const count = countByTab(key);
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`mc-loc-tab${isActive ? ' is-active' : ''}`}
                  aria-pressed={isActive}
                  onClick={() => setActiveTab(key)}
                >
                  {label}
                  {count > 0 && <span className="mc-loc-tab-count">{count}</span>}
                </button>
              );
            })}
          </div>

          {/* Liste */}
          {isLoading ? (
            <div className="mc-loc-list" aria-busy="true" aria-label="Chargement">
              <div className="mc-loc-skeleton"></div>
              <div className="mc-loc-skeleton"></div>
              <div className="mc-loc-skeleton"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="mc-loc-empty">
              <span className="mc-loc-empty-icon" aria-hidden="true">
                <i className="bi bi-calendar-x"></i>
              </span>
              <h2>Aucune réservation</h2>
              <p>Aucune réservation dans cette catégorie.</p>
              <Link to="/" className="mc-btn mc-btn--primary mc-btn--sm">
                Voir les annonces
              </Link>
            </div>
          ) : (
            <ul className="mc-loc-list">
              {filtered.map((resa) => {
                const badge = STATUS_BADGE[resa.status] ?? {
                  tone: 'neutral',
                  label: resa.status,
                };
                const vehicleName = [
                  resa.Vehicle?.VehicleModel?.marque,
                  resa.Vehicle?.VehicleModel?.modele,
                ]
                  .filter(Boolean)
                  .join(' ');
                const hasPrice = resa.totalPrice != null && resa.totalPrice !== '';

                return (
                  <li key={resa.reservationId}>
                    <Link
                      to={`/inbox/messages/thread/${resa.reservationId}`}
                      className="mc-loc-card"
                    >
                      {/* Photo véhicule */}
                      <img
                        src={resa.Vehicle?.principalPhotos || '/char.svg'}
                        alt=""
                        className="mc-loc-photo"
                        loading="lazy"
                      />

                      {/* Infos */}
                      <div className="mc-loc-body">
                        <div className="mc-loc-top">
                          <h2 className="mc-loc-title">{vehicleName || 'Véhicule'}</h2>
                          <span className={`mc-loc-status mc-loc-status--${badge.tone}`}>
                            {badge.label}
                          </span>
                        </div>

                        <p className="mc-loc-meta">
                          <span>
                            <i className="bi bi-person" aria-hidden="true"></i>
                            {resa.Invite?.firstName} {resa.Invite?.lastName}
                          </span>
                          <span>
                            <i className="bi bi-calendar3" aria-hidden="true"></i>
                            {moment(resa.startDate).format('DD MMM')} →{' '}
                            {moment(resa.endDate).format('DD MMM YYYY')}
                            <span className="mc-loc-days">
                              ({formatDays(resa.startDate, resa.endDate)})
                            </span>
                          </span>
                        </p>

                        <div className="mc-loc-foot">
                          {hasPrice ? (
                            <span className="mc-loc-price">
                              {parseFloat(resa.totalPrice).toFixed(2)} €
                            </span>
                          ) : (
                            <span></span>
                          )}
                          <span className="mc-loc-go">
                            Ouvrir la conversation
                            <i className="bi bi-arrow-right" aria-hidden="true"></i>
                          </span>
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
}

export default Locations;
